import React, { useEffect, useState } from 'react';
import { useParams, useFetcher } from 'react-router-dom';
import WarningIcon from '@mui/icons-material/Warning';
import { Button, Stack, Divider } from '@mui/material';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import { MemberRepository } from 'src/api/member_repository';
import { FeedbackPopup, FeedbackType } from 'src/components/feedback_popup';
import { DataTable } from 'src/components/table';
import { useInvitations } from 'src/hooks/use_invitations';
import { InvitationFieldsFragment, RoleFieldsFragment } from 'src/types';
import { MemberAction } from 'src/types/loaders';
import { InvitationInput } from 'src/types/member_management';
import { notifyError, notifySuccess, getInvitationValidTime } from 'src/utils';

// Column identifiers for invitations table
enum InvitationColumns {
  Member = 'INVITATION_EMAIL',
  Status = 'INVITATION_STATUS',
  ValidTime = 'INVITATION_VALID_TIME',
  CreatedBy = 'INVITATION_CREATED_BY',
  Action = 'INVITATION_ACTIONS',
}

// Create column helper for typed columns
const columnHelper = createColumnHelper<InvitationFieldsFragment>();

export type InvitationListProps = {
  memberRepository: MemberRepository;
  isBackgroundMode?: boolean;
};

export const InvitationList: React.FC<InvitationListProps> = ({
  memberRepository,
  isBackgroundMode = false,
}) => {
  const { invitations, totalCount, page, perPage, handlePageChange } =
    useInvitations(
      memberRepository,
      !isBackgroundMode, // Pass useLoader=true for normal mode, false for background mode
    );
  const { organization } = useParams();
  const [revokeDialog, setRevokeDialog] = useState({
    open: false,
    inviteId: '',
    email: '',
  });
  const fetcher = useFetcher();

  const handleRevokeClick = (inviteId: string, email: string) => {
    setRevokeDialog({
      open: true,
      inviteId,
      email,
    });
  };
  const handleCloseDialog = () => {
    setRevokeDialog({ open: false, inviteId: '', email: '' });
  };

  const handleConfirmRevoke = async () => {
    const formData = new FormData();
    formData.append('actionType', MemberAction.REVOKE_INVITE);
    formData.append('inviteId', revokeDialog.inviteId);
    fetcher.submit(formData, {
      method: 'POST',
      action: `/${organization}/invites`,
    });
    handleCloseDialog();
  };

  const handleResendInvitation = async (
    email: string,
    roles: RoleFieldsFragment[],
  ) => {
    const formData = new FormData();
    formData.append('actionType', MemberAction.INVITE_USERS);

    const inviteInput = new InvitationInput(
      email,
      roles.map((role) => role.id),
    );
    formData.append('invites', JSON.stringify([inviteInput]));

    await fetcher.submit(formData, {
      method: 'POST',
      action: `/${organization}/invites`,
    });
  };

  const isInvitationExpired = (expiresAt?: string | null): boolean => {
    if (!expiresAt) return true;
    const expiryDate = new Date(expiresAt);
    return expiryDate <= new Date();
  };

  // Handle actions results
  useEffect(() => {
    if (fetcher.data && fetcher.state === 'idle') {
      const actionResult = fetcher.data;

      if (actionResult.action === MemberAction.REVOKE_INVITE) {
        if (actionResult.success) {
          notifySuccess('Invitation revoked successfully.');
        } else {
          notifyError('Failed to revoke invitation. Please try again later.');
        }
      } else if (actionResult.action === MemberAction.INVITE_USERS) {
        if (actionResult.success) {
          notifySuccess('Invitation sent successfully.');
        } else if (actionResult.invitationErrors) {
          notifyError(actionResult.invitationErrors[0].message);
        }
      }
    }
  }, [fetcher.data, fetcher.state]);

  // Helper functions to get columns and headers
  const getInvitationColumns = (): ColumnDef<
    InvitationFieldsFragment,
    string
  >[] => [
    columnHelper.accessor('email', {
      id: InvitationColumns.Member,
      header: 'Member',
      size: 300,
      cell: (info) => info.getValue() || '',
    }),
    columnHelper.display({
      id: InvitationColumns.Status,
      header: 'Status',
      minSize: 100,
      maxSize: 150,
      size: 120,
      cell: (props) => {
        const invitation = props.row.original;
        const expired = isInvitationExpired(invitation.expiresAt);
        return expired ? 'Expired' : 'Pending';
      },
    }),
    columnHelper.display({
      id: InvitationColumns.ValidTime,
      header: 'Valid Time',
      minSize: 120,
      maxSize: 180,
      size: 150,
      cell: (props) => {
        const invitation = props.row.original;
        return getInvitationValidTime(invitation.expiresAt);
      },
    }),
    columnHelper.accessor('inviterName', {
      id: InvitationColumns.CreatedBy,
      header: 'Created By',
      size: 300,
      cell: (info) => info.getValue() || 'N/A',
    }),
    columnHelper.display({
      id: InvitationColumns.Action,
      header: 'Action',
      minSize: 220,
      maxSize: 220,
      cell: (props) => {
        const invitation = props.row.original;
        const expired = isInvitationExpired(invitation.expiresAt);

        return (
          <Stack
            direction='row'
            sx={{
              justifyContent: 'flex-start',
              ml: -1,
            }}
          >
            {expired && (
              <>
                <Button
                  variant='text'
                  color='primary'
                  onClick={() =>
                    handleResendInvitation(
                      invitation.email,
                      invitation.roles as RoleFieldsFragment[],
                    )
                  }
                >
                  RESEND
                </Button>
                <Divider
                  orientation='vertical'
                  sx={{ height: '14px', alignSelf: 'center' }}
                />
              </>
            )}
            <Button
              variant='text'
              color='primary'
              onClick={() =>
                handleRevokeClick(invitation.id, invitation.email || '')
              }
            >
              {expired ? 'REMOVE' : 'REVOKE INVITATION'}
            </Button>
          </Stack>
        );
      },
    }),
  ];

  return (
    <>
      <DataTable
        data={invitations}
        columns={getInvitationColumns()}
        paginationProps={{
          pageSize: perPage,
          currentPage: page,
          itemCount: totalCount,
          onPageSelected: handlePageChange,
        }}
        ariaLabel='invitations table'
      />
      <FeedbackPopup
        data={{
          primaryButtonText: 'REVOKE INVITATION',
          secondaryButtonText: 'NO',
          title: 'Revoke Invitation',
          subtitle: `Are you sure you want to revoke the invitation for ${revokeDialog.email}?`,
          icon: WarningIcon,
          type: FeedbackType.Error,
        }}
        isDialogOpen={revokeDialog.open}
        onPrimaryButtonClick={handleConfirmRevoke}
        onSecondaryButtonClick={handleCloseDialog}
        handleDialogClose={handleCloseDialog}
      />
    </>
  );
};
